import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { renderToStaticMarkup } from 'react-dom/server';
import { FirstNameComponent } from './Hero/components/FirstNameComponent.js';
import { LastNameComponent } from './Hero/components/LastNameComponent.js';
import { getLeafColliders, shakePalm } from './Palm/leafColliders.js';
import { addSource } from '../utils/cursorFx.js';
import { FRAME_ORDER, onFrame } from '../utils/frameLoop.js';
import { HERO_MIN_SCALE, HERO_SHRINK_PX } from '../utils/heroRunway.js';

/**
 * Full-page physics canvas for the landing sequence.
 *
 * Every physics object is a 1:1-scale first or last name (the size the word
 * has inside the loaded hero name). The loaded name converts into two bodies
 * at their exact rects and drops to a split floor at the hero fold; more
 * KEVIN + RUFINO rows then stack on top (hero-style, with a -5% overlap)
 * until the header viewport is full.
 *
 * Physics runs in full document coordinates (y = 0 at the top of the page), so
 * when the floor splits open on scroll the pile falls all the way down the page
 * and straight off the bottom of it — there is no catch floor and no footer
 * obstacle, and each name is retired from the world as it clears the document,
 * so the simulation winds down to nothing instead of holding a settled pile
 * for the rest of the session. The canvas itself stays fixed and
 * viewport-sized (every body is invisible — only the sprites drawn each
 * frame show), and the sprite draw is offset by scrollY to render just
 * the visible slice, avoiding a document-tall backing store.
 */
const ACID = '#F1F43B';
const ULTRA = '#3e3bf4';
// Fully transparent, spelled as a colour so the SVG's `fill` still parses.
const CLEAR = '#00000000';

// ── Firework burst (ported from the retired MatterJSCanvas) ──────────────────
// Clicking a name sprite in the pile pops a pixel/cell firework at the hit
// point. Bursts are stored in document coordinates (like the bodies) and drawn
// inside the same -scrollY offset transform as the sprites.
const FIREWORK_DURATION = 760;
const FIREWORK_CELL = 9;
const FIREWORK_LIMIT = 10;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const smoothstep = (edge0, edge1, value) => {
  const x = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return x * x * (3 - 2 * x);
};

const easeOutCubic = value => 1 - Math.pow(1 - value, 3);

const drawFireworkCell = (ctx, x, y, size, shape) => {
  if (shape === 'circle') {
    ctx.beginPath();
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  const radius = size * 0.36;
  const left = x - size / 2;
  const top = y - size / 2;

  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(left, top, size, size, radius);
    ctx.fill();
    return;
  }

  ctx.fillRect(left, top, size, size);
};

const makeFireworkCells = () => {
  const cells = [];
  const rotation = Math.random() * Math.PI * 2;
  const spokes = 8;

  for (let spoke = 0; spoke < spokes; spoke += 1) {
    const angle = rotation + (Math.PI * 2 * spoke) / spokes;
    for (let step = 1; step <= 6; step += 1) {
      cells.push({
        angle,
        distance: FIREWORK_CELL * (step * 1.65 + (spoke % 2) * 0.35),
        delay: step * 18,
        size: FIREWORK_CELL * (step < 3 ? 1.04 : 0.82),
        color: (spoke + step) % 3 === 0 ? ACID : ULTRA,
        shape: step % 2 === 0 ? 'circle' : 'squircle',
      });
    }
  }

  for (let ring = 2; ring <= 5; ring += 1) {
    const points = ring * 6;
    for (let point = 0; point < points; point += 1) {
      if ((point + ring) % 3 === 0) continue;

      cells.push({
        angle:
          rotation +
          (Math.PI * 2 * point) / points +
          (ring % 2 ? Math.PI / points : 0),
        distance: FIREWORK_CELL * ring * 2.2,
        delay: ring * 24,
        size: FIREWORK_CELL * (ring % 2 ? 0.72 : 0.6),
        color: (point + ring) % 4 === 0 ? ACID : ULTRA,
        shape: (point + ring) % 2 === 0 ? 'circle' : 'squircle',
      });
    }
  }

  cells.push(
    {
      angle: 0,
      distance: 0,
      delay: 0,
      size: FIREWORK_CELL * 1.2,
      color: ACID,
      shape: 'circle',
    },
    {
      angle: 0,
      distance: 0,
      delay: 40,
      size: FIREWORK_CELL * 0.78,
      color: ULTRA,
      shape: 'squircle',
    },
  );

  return cells;
};

const drawFireworks = (ctx, fireworks, now) => {
  for (let i = fireworks.length - 1; i >= 0; i -= 1) {
    const burst = fireworks[i];
    const age = now - burst.start;
    if (age > FIREWORK_DURATION) {
      fireworks.splice(i, 1);
      continue;
    }

    const progress = clamp(age / FIREWORK_DURATION, 0, 1);
    const baseAlpha =
      clamp(progress / 0.14, 0, 1) * (1 - smoothstep(0.64, 1, progress));

    ctx.save();
    ctx.translate(burst.x, burst.y);
    ctx.globalCompositeOperation = 'source-over';

    for (const cell of burst.cells) {
      const localAge = age - cell.delay;
      if (localAge < 0) continue;

      const localProgress = clamp(
        localAge / (FIREWORK_DURATION - cell.delay),
        0,
        1,
      );
      const travel = cell.distance * easeOutCubic(localProgress);
      const x =
        Math.round((Math.cos(cell.angle) * travel) / FIREWORK_CELL) *
        FIREWORK_CELL;
      const y =
        Math.round((Math.sin(cell.angle) * travel) / FIREWORK_CELL) *
        FIREWORK_CELL;
      const size = cell.size * (1 - localProgress * 0.22);
      const alpha =
        baseAlpha *
        clamp(localAge / 90, 0, 1) *
        (1 - smoothstep(0.72, 1, localProgress));

      if (alpha <= 0) continue;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = cell.color;
      drawFireworkCell(ctx, x, y, size, cell.shape);
    }

    ctx.restore();
  }
  ctx.globalAlpha = 1;
};

// Word geometry within HeroName's 1402×187 viewBox:
// KEVIN = 600 units left-anchored, RUFINO = 780 units right-anchored (22 gap).
const NAME_VB_W = 1402;
const FIRST_VB = { w: 600, h: 187 };
const LAST_VB = { w: 780, h: 187 };
const FIRST_FRAC = FIRST_VB.w / NAME_VB_W;
const LAST_FRAC = LAST_VB.w / NAME_VB_W;

// ── Tunables ─────────────────────────────────────────────────────────────────
const CHECK_MS = 150; // how often to poll row-0 landing / all-rows settled
const OVERLAP_FRAC_DESKTOP = 0.05; // row overlap as fraction of name width — matches the hero's mt-[-5%]
const OVERLAP_FRAC_MOBILE = 0.01; // shallower interlock on thin viewports → fewer rows to fill the fold
const MOBILE_MAX_W = 768; // viewports narrower than this use the mobile overlap
const SETTLE_SPEED = 10; // a row is "settled" once both bodies are this slow
const ROW_CAP = 40; // sanity cap on the computed row count
const GRAVITY_FILL = 1.8; // heavier gravity while the stack drops, so the fill reads fast
const FILL_TIMEOUT_MS = 7000; // report "filled" even if a body never quite settles
const FLOOR_T = 16; // floor bar thickness (collision only — invisible)
// Fraction of the width that stays solid. The floor opens to the right only,
// so the pile slides along the fixed left section and exits down the right
// edge rather than dropping straight through a widening centre gap.
const FLOOR_SPLIT = 0.42;
const DRAIN_TICK_MS = 400; // wake/cull cadence while the floor is open
// Leaf colliders: one static circle per frond sample. The palm emits three
// samples for each of its ten fronds, so thirty is the exact count.
const LEAF_COUNT = 30;
const LEAF_RADIUS = 12;
// The fronds deflect the pile on its way past, then get out of the way. Long
// enough to read as a bounce; short enough that nothing sits parked in the
// palm waiting for the window to close, which at five seconds it visibly did.
const LEAF_WINDOW_MS = 2000;
const LEAF_PARK = { x: -5000, y: -5000 };
const CULL_MARGIN = 600; // px past the document bottom before a name is retired
// A name landing on a frond shakes the tree; a pile grinding along one must
// not hold it there. Long enough for the shake to ring out and be re-triggered
// by the next name rather than the next contact.
const SHAKE_THROTTLE_MS = 260;
const IMPULSE_RADIUS_FRAC = 0.28; // click impulse reach, as a fraction of viewport width
const IMPULSE_UP = 12; // upward kick strength on click
const IMPULSE_PUSH = 8; // radial push strength on click

// Rasterize a name-word colorway to a decoded Image for canvas drawing.
const svgToImage = (element, vb) => {
  const markup = renderToStaticMarkup(element).replace(
    '<svg',
    `<svg width="${vb.w}" height="${vb.h}"`,
  );
  const url = URL.createObjectURL(
    new Blob([markup], { type: 'image/svg+xml' }),
  );
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = err => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
};

const FillPhysicsCanvas = ({
  active,
  getSpawnRect,
  onHandoff,
  onFilled,
  onDrained,
}) => {
  const canvasRef = useRef(null);
  const onHandoffRef = useRef(onHandoff);
  const onFilledRef = useRef(onFilled);
  const onDrainedRef = useRef(onDrained);
  onHandoffRef.current = onHandoff;
  onFilledRef.current = onFilled;
  onDrainedRef.current = onDrained;

  useEffect(() => {
    if (!active) return undefined;
    const canvas = canvasRef.current;
    let cancelled = false;
    let teardown = () => {};

    // Colorways follow NameInstance's convention:
    // Component(primaryColor=outline, secondaryColor=fill)
    //
    // `filled` is solid ultra — the loader's end state. The other variant
    // leaves the letterforms CLEAR rather than acid: painted yellow they only
    // ever matched the hero's own ground, and the moment the pile left it the
    // names carried a patch of the hero down onto the paper with them. Empty,
    // the counters show whatever the names happen to be falling across.
    Promise.all([
      import('matter-js'),
      svgToImage(
        <FirstNameComponent primaryColor={ULTRA} secondaryColor={ULTRA} />,
        FIRST_VB,
      ),
      svgToImage(
        <FirstNameComponent primaryColor={ULTRA} secondaryColor={CLEAR} />,
        FIRST_VB,
      ),
      svgToImage(
        <LastNameComponent primaryColor={ULTRA} secondaryColor={ULTRA} />,
        LAST_VB,
      ),
      svgToImage(
        <LastNameComponent primaryColor={ULTRA} secondaryColor={CLEAR} />,
        LAST_VB,
      ),
    ]).then(
      ([
        { default: Matter },
        firstFilled,
        firstOutline,
        lastFilled,
        lastOutline,
      ]) => {
        if (cancelled || !canvas) return;

        let W = window.innerWidth;
        let fold = window.innerHeight; // the hero fold — where the floor sits
        // Names are retired at the top of the projects section, not at the end
        // of the document. They belong to the landing: they fall through the
        // hero and the intro and off the page, and they must never reach the
        // works pane or the footer — both of which they would otherwise be
        // drawn over, since this canvas sits above the sections.
        //
        // The boundary is always below the viewport while the reader is still
        // in the intro, so nothing is ever seen to vanish.
        const contentEnd = () => {
          const works = document.getElementById('projects');
          if (!works) return Math.max(document.body.scrollHeight, fold);
          return Math.max(
            works.getBoundingClientRect().top + window.scrollY,
            fold,
          );
        };
        let docH = contentEnd();

        const engine = Matter.Engine.create({ enableSleeping: true });
        // Heavier gravity while the stack drops in, so the fill animation is
        // quick; reset to normal once every row has landed.
        engine.gravity.y = GRAVITY_FILL;
        const world = engine.world;
        // The canvas stays viewport-sized and fixed; every physics body is
        // invisible (only the sprites drawn below show), so instead of
        // allocating a document-tall canvas we render the viewport slice and
        // offset the sprite draw by scrollY. Physics still runs in full
        // document coordinates.
        //
        // No Matter.Render and no Matter.Runner.run: each of those runs a
        // requestAnimationFrame chain of its own, and Render also traced a
        // transparent path for every body — walls, floors and thirty leaf
        // circles — every frame, only for none of it to show. The runner is
        // kept for its fixed-timestep accounting and ticked from the page's
        // shared loop; the sprites are the only thing drawn.
        canvas.width = W;
        canvas.height = fold;
        const ctx = canvas.getContext('2d');
        const runner = Matter.Runner.create();

        const staticOpts = { isStatic: true };

        // Split floor at the fold: two invisible halves meeting at the center.
        // Scroll slides them outward to open a center gap.
        const floorY = fold - FLOOR_T / 2;
        const floorOpts = {
          isStatic: true,
          friction: 0.05, // slippery so the pile slides off as it retracts
        };
        // The left section never moves; only the right one slides away.
        let leftW = W * FLOOR_SPLIT;
        let rightW = W - leftW;
        const leftFloor = Matter.Bodies.rectangle(
          leftW / 2,
          floorY,
          leftW,
          FLOOR_T,
          floorOpts,
        );
        const rightFloor = Matter.Bodies.rectangle(
          leftW + rightW / 2,
          floorY,
          rightW,
          FLOOR_T,
          floorOpts,
        );

        // Tall walls, so the pile stays within the page width for the whole
        // fall even as the document grows. There is deliberately no floor at
        // the bottom: the names fall past the footer and off the page, and are
        // culled once clear of it (see the drain block below).
        const wallH = 40000;
        const leftWall = Matter.Bodies.rectangle(-30, 0, 60, wallH, staticOpts);
        const rightWall = Matter.Bodies.rectangle(
          W + 30,
          0,
          60,
          wallH,
          staticOpts,
        );
        Matter.World.add(world, [leftFloor, rightFloor, leftWall, rightWall]);

        // Track the real document height as content lazy-loads in and the page
        // grows (images/videos below the fold change it) — it sets the depth a
        // falling name has to clear before it can be retired.
        const docObserver = new ResizeObserver(() => {
          const h = contentEnd();
          if (h === docH) return;
          docH = h;
        });
        docObserver.observe(document.body);

        const sprites = []; // { body, img, width, height }

        // Firework bursts (in document coords) fired when a name sprite is
        // clicked. Suppressed under reduced-motion, matching the retired canvas.
        const fireworks = [];
        const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        let reduceMotion = motionQuery.matches;
        const handleMotionPreference = () => {
          reduceMotion = motionQuery.matches;
          if (reduceMotion) fireworks.length = 0;
        };
        motionQuery.addEventListener('change', handleMotionPreference);

        // 1:1 geometry of the loaded name. getSpawnRect returns a viewport
        // rect; convert to document space (at handoff scrollY≈0, but be safe).
        const vrect = getSpawnRect?.();
        const rect = vrect
          ? {
              left: vrect.left,
              right: vrect.right,
              top: vrect.top + window.scrollY,
              width: vrect.width,
              height: vrect.height,
            }
          : {
              left: 0,
              right: W,
              top: fold * 0.35,
              width: W,
              height: W * (187 / NAME_VB_W),
            };
        const firstW = rect.width * FIRST_FRAC;
        const lastW = rect.width * LAST_FRAC;
        const wordH = rect.height;

        // Hero-style row overlap: collision boxes are shorter than the drawn
        // sprites, so stacked rows visually interlock by `overlapPx` (like the
        // hero's mt-[-5%]) while the physics stays box-on-box. Sprites are
        // drawn bottom-aligned to their box so the bottom row sits exactly on
        // the floor instead of dipping into it. Thin viewports interlock less
        // so it takes fewer of the (narrower, shorter) rows to fill the fold.
        const overlapFrac =
          W < MOBILE_MAX_W ? OVERLAP_FRAC_MOBILE : OVERLAP_FRAC_DESKTOP;
        const overlapPx = rect.width * overlapFrac;
        const boxH = wordH - overlapPx;
        const drawTop = boxH / 2 - wordH; // sprite top in body-local coords

        // Rows needed for the stack to fill exactly one viewport: each row
        // adds one collision-box height to the pile.
        const totalRows = Math.min(ROW_CAP, Math.max(1, Math.ceil(fold / boxH)));

        // Word x-anchors, identical for every row (matches the hero's NameRow
        // layout: first name left-anchored, last name right-anchored).
        const firstX = rect.left + firstW / 2;
        const lastX = rect.right - lastW / 2;

        // Low friction matters for the drain: with grippy planks the stack
        // cantilevers on tiny floor ledges and jams in a solver equilibrium
        // instead of sliding through the opening gap. Rows still stack neatly
        // on landing because they fall with zero horizontal velocity.
        const addSprite = (img, x, y, w) => {
          const body = Matter.Bodies.rectangle(x, y, w, boxH, {
            restitution: 0.05,
            friction: 0.15,
            frictionAir: 0.012,
          });
          // Tetris mode while the stack drops in: rotation locked so the
          // boosted-gravity cascade can't knock rows into a tumble — they
          // stack flat every time. Real inertia is restored once filled, so
          // the scroll drain and click impulses still tumble naturally.
          const baseInertia = body.inertia;
          Matter.Body.setInertia(body, Infinity);
          Matter.World.add(world, body);
          sprites.push({ body, img, width: w, height: wordH, baseInertia });
          return body;
        };

        // The loaded name becomes physical: KEVIN (left-anchored) and RUFINO
        // (right-anchored) as row 0, placed so each drawn sprite covers the DOM
        // name's exact rect (sprite bottom = box bottom = rect bottom).
        const handoffY = rect.top + wordH - boxH / 2;
        const rows = [
          [
            addSprite(firstFilled, firstX, handoffY, firstW),
            addSprite(lastFilled, lastX, handoffY, lastW),
          ],
        ];

        // ── Scroll-driven floor split ────────────────────────────────────────
        // Sleeping bodies ignore gravity, so a sleeping pile levitates when the
        // floor slides out from under it. While the floor is open at all,
        // disable sleeping outright and keep waking the pile on a watchdog — a
        // single wake per scroll event proved unreliable (the pile froze
        // mid-air over an open gap). Sleeping is only needed while the floor is
        // closed, for the fill gauge.
        let lastShift = 0;
        let opened = false;

        // How big the pile is drawn. One while it sits at full size in the
        // hero, easing to HERO_MIN_SCALE across the shrink runway, then held
        // there — once the bodies themselves have been scaled at the handoff,
        // the drawing is 1:1 with the physics again.
        let spriteScale = 1;
        const shrinkNow = () =>
          Math.min(1, Math.max(0, window.scrollY / HERO_SHRINK_PX));
        const drawScale = () =>
          opened ? 1 : 1 - (1 - HERO_MIN_SCALE) * shrinkNow();

        const wakeAll = () => {
          for (const { body } of sprites) Matter.Sleeping.set(body, false);
        };
        const updateFloor = () => {
          // 0 while the pile is still shrinking in place, 1 once the runway
          // is spent and it is time for it to go.
          const p = window.scrollY >= HERO_SHRINK_PX ? 1 : 0;
          // Leaving the hero takes the box apart: the floor and the right
          // wall are removed outright, and only the left wall stays. From then
          // on the pile is held up by nothing, and the only things in its way
          // are the intro's copy column and the palm's fronds — so it falls
          // down and, with nowhere to stop on the right, off that edge.
          engine.enableSleeping = p === 0;
          if (p > 0 && !opened) {
            opened = true;
            // Until now the pile has been drawn pinned to the viewport while
            // the page scrolled behind it, and scaled about the middle of the
            // screen. Move and scale the bodies to match exactly where they
            // were last drawn, so the moment it starts to fall is the one
            // frame where nothing jumps.
            const k = HERO_MIN_SCALE;
            const cx = W / 2;
            const cy = fold / 2;
            const drop = window.scrollY;
            for (const sp of sprites) {
              const b = sp.body;
              Matter.Body.setPosition(b, {
                x: cx + (b.position.x - cx) * k,
                y: cy + (b.position.y - cy) * k + drop,
              });
              Matter.Body.scale(b, k, k);
            }
            spriteScale = k;
            Matter.World.remove(world, [leftFloor, rightFloor, rightWall]);
            // A pronounced lean, so the pile tips off whatever it lands on
            // rather than balancing there. Gently sloped, names sat on the
            // copy column for ten seconds or more before finding an edge.
            engine.gravity.x = 0.7;
            engine.gravity.y = 1.5;
          }
          lastShift = p > 0 ? 1 : 0;
          if (p > 0) wakeAll();
        };
        updateFloor();
        window.addEventListener('scroll', updateFloor, { passive: true });

        // ── Draining off the page ────────────────────────────────────────────
        // Nothing catches the pile at the bottom, so every name eventually
        // clears the document. Once one is far enough past the end to be
        // invisible at any scroll position it is removed from the world: the
        // solver steps fewer bodies and the frame draws fewer sprites the
        // further the drain gets, and when the last one is gone it leaves the
        // shared loop outright rather than stepping a settled pile for the
        // rest of the session.
        let stopped = false;
        let leaveLoop = () => {};
        const culled = new WeakSet();
        const stopSimulation = () => {
          if (stopped) return;
          stopped = true;
          leaveLoop();
          ctx.clearRect(0, 0, W, fold);
          // The landing is over: the hero can be taken out of the document now
          // without interrupting anything the reader was watching.
          onDrainedRef.current?.();
        };
        const cullFallen = () => {
          const limit = docH + CULL_MARGIN;
          for (let i = sprites.length - 1; i >= 0; i--) {
            const { body } = sprites[i];
            if (body.position.y < limit) continue;
            culled.add(body);
            Matter.World.remove(world, body);
            sprites.splice(i, 1);
          }
          if (sprites.length === 0) stopSimulation();
        };

        // The floor only opens on scroll, and scroll is frozen until the fill
        // has finished — so `lastShift > 0` means the pile is draining and
        // every row already exists.
        const drainTimer = setInterval(() => {
          if (lastShift <= 0 || stopped) return;
          engine.enableSleeping = false;
          wakeAll();
          cullFallen();
        }, DRAIN_TICK_MS);

        // Static circles tracking the palm's fronds, parked far off-page and
        // moved into place each step while the palm is in its intro pose. The
        // palm registers itself as the provider; with no palm mounted the
        // provider returns nothing and these simply stay parked.
        const leafBodies = Array.from({ length: LEAF_COUNT }, () =>
          Matter.Bodies.circle(LEAF_PARK.x, LEAF_PARK.y, LEAF_RADIUS, {
            isStatic: true,
            restitution: 0.42,
            friction: 0.02,
          }),
        );
        Matter.World.add(world, leafBodies);

        // A name that hits a frond shakes the tree.
        //
        // The collision is already in the solver — the names visibly deflect
        // off the palm on their way down — so the only thing missing was the
        // palm being told. Throttled, because a pile sliding along a frond
        // generates contacts every step, and re-arming the shake on each of
        // them would hold the tree at full amplitude instead of letting it
        // ring out.
        const leafSet = new Set(leafBodies);
        let lastShakeAt = 0;
        Matter.Events.on(engine, 'collisionStart', event => {
          if (reduceMotion) return;
          const now = performance.now();
          if (now - lastShakeAt < SHAKE_THROTTLE_MS) return;
          for (const pair of event.pairs) {
            if (!leafSet.has(pair.bodyA) && !leafSet.has(pair.bodyB)) continue;
            lastShakeAt = now;
            shakePalm();
            return;
          }
        });

        // The intro's copy column, as a solid body. The names must never end
        // up over the text, and the honest way to guarantee that is to make
        // the text something they cannot pass through. Parked off-page until
        // the intro is actually on screen.
        const copyBody = Matter.Bodies.rectangle(
          LEAF_PARK.x,
          LEAF_PARK.y,
          10,
          10,
          {
            isStatic: true,
            // Almost frictionless: this body exists to keep names off the
            // text, not to collect them.
            friction: 0.001,
            restitution: 0.15,
          },
        );
        Matter.World.add(world, copyBody);
        let copySize = { w: 10, h: 10 };

        const trackCopyColumn = () => {
          const copy = document.querySelector('[data-palm-clip]');
          const intro = document.getElementById('intro');
          if (!copy || !intro || !opened) {
            if (copyBody.position.x !== LEAF_PARK.x) {
              Matter.Body.setPosition(copyBody, LEAF_PARK);
            }
            return;
          }
          const r = copy.getBoundingClientRect();
          if (r.height < 2 || r.bottom < 0 || r.top > window.innerHeight) {
            if (copyBody.position.x !== LEAF_PARK.x) {
              Matter.Body.setPosition(copyBody, LEAF_PARK);
            }
            return;
          }
          if (
            Math.abs(r.width - copySize.w) > 2 ||
            Math.abs(r.height - copySize.h) > 2
          ) {
            Matter.Body.scale(
              copyBody,
              r.width / copySize.w,
              r.height / copySize.h,
            );
            copySize = { w: r.width, h: r.height };
          }
          Matter.Body.setPosition(copyBody, {
            x: r.left + r.width / 2,
            y: r.top + r.height / 2 + window.scrollY,
          });
        };

        let drainStart = 0;
        Matter.Events.on(engine, 'beforeUpdate', () => {
          trackCopyColumn();
          if (!drainStart && lastShift > 0) drainStart = performance.now();
          // Live only for the length of the fall. The fronds are there to
          // deflect the pile on its way past, and a static circle a name can
          // deflect off is also one it can come to rest on — left permanently
          // live, the pile ended up parked in the palm and never cleared.
          const live =
            drainStart && performance.now() - drainStart < LEAF_WINDOW_MS;
          const colliders = live ? getLeafColliders(performance.now()) : [];
          for (let i = 0; i < leafBodies.length; i++) {
            const c = colliders[i];
            const body = leafBodies[i];
            if (!c) {
              if (body.position.x !== LEAF_PARK.x) {
                Matter.Body.setPosition(body, LEAF_PARK);
              }
              continue;
            }
            const scale = c.r / body.circleRadius;
            if (Math.abs(scale - 1) > 0.02) {
              Matter.Body.scale(body, scale, scale);
              body.circleRadius = c.r;
            }
            Matter.Body.setPosition(body, { x: c.x, y: c.y });
          }
        });

        // Draw the name sprites each frame, bottom-aligned to the (shorter)
        // collision box. Bodies live in document coordinates; the canvas is a
        // fixed viewport slice, so shift everything up by scrollY.
        const draw = () => {
          ctx.clearRect(0, 0, W, fold);

          // Two regimes.
          //
          // Before the pile is released it is pinned to the viewport and
          // scaled about the middle of the screen: the page scrolls behind it
          // while it shrinks in place, which is what makes the hero feel
          // elongated rather than simply tall.
          //
          // After release the bodies have been moved and scaled to match, so
          // this goes back to plain document-space drawing and the physics is
          // what moves them.
          const offset = opened ? window.scrollY : 0;
          const k = drawScale();
          const cx = W / 2;
          const cy = fold / 2;

          ctx.save();
          ctx.translate(0, -offset);
          for (const { body, img, width, height } of sprites) {
            ctx.save();
            if (opened) {
              ctx.translate(body.position.x, body.position.y);
            } else {
              // Scale the whole composition toward the centre of the screen,
              // not each name about its own middle — the pile has to hold its
              // arrangement as it shrinks, or it just develops gaps.
              ctx.translate(
                cx + (body.position.x - cx) * k,
                cy + (body.position.y - cy) * k,
              );
            }
            ctx.rotate(body.angle);
            const w = width * (opened ? spriteScale : k);
            const h = height * (opened ? spriteScale : k);
            const top = drawTop * (opened ? spriteScale : k);
            ctx.drawImage(img, -w / 2, top, w, h);
            ctx.restore();
          }
          if (fireworks.length > 0) {
            drawFireworks(ctx, fireworks, performance.now());
          }
          ctx.restore();
        };

        // The names are canvas, so they cannot carry a hover of their own.
        // They answer the cursor through a source instead: asked what is at a
        // point, this reports the sprite drawn there, and the chip says what
        // clicking it will do.
        //
        // The box is the one the sprite is actually DRAWN in, not the body's,
        // because before the pile is released the two are different — the pile
        // is pinned to the viewport and scaled while the page moves behind it.
        const spriteBoxAt = (clientX, clientY) => {
          const k = drawScale();
          const scale = opened ? spriteScale : k;
          const offset = opened ? window.scrollY : 0;
          const cx = W / 2;
          const cy = fold / 2;
          for (let i = sprites.length - 1; i >= 0; i--) {
            const sp = sprites[i];
            const b = sp.body;
            const bx = opened ? b.position.x : cx + (b.position.x - cx) * k;
            const by = opened ? b.position.y : cy + (b.position.y - cy) * k;
            const w = sp.width * scale;
            const h = sp.height * scale;
            const left = bx - w / 2;
            const top = by - offset + drawTop * scale;
            if (
              clientX >= left &&
              clientX <= left + w &&
              clientY >= top &&
              clientY <= top + h
            ) {
              return { left, top, right: left + w, bottom: top + h };
            }
          }
          return null;
        };

        const dropSource = addSource((x, y) => {
          if (stopped || sprites.length === 0) return null;
          const geom = spriteBoxAt(x, y);
          return geom ? { label: 'explode', geom } : null;
        });

        // Click anywhere to poke the pile: radial + upward impulse on nearby
        // names. Canvas is pointer-events:none, so listen on the document and
        // translate the click into document coordinates. Never preventDefault,
        // so links and buttons keep working.
        const handleClick = e => {
          const cx = e.clientX;
          const cy = e.clientY + window.scrollY;

          // Pop a firework only when the click lands on an actual name sprite.
          if (!reduceMotion) {
            const hit = Matter.Query.point(
              sprites.map(s => s.body),
              { x: cx, y: cy },
            );
            if (hit.length > 0) {
              fireworks.push({
                x: cx,
                y: cy,
                start: performance.now(),
                cells: makeFireworkCells(),
              });
              if (fireworks.length > FIREWORK_LIMIT) fireworks.shift();
            }
          }

          const radius = W * IMPULSE_RADIUS_FRAC;
          for (const { body } of sprites) {
            const dx = body.position.x - cx;
            const dy = body.position.y - cy;
            const dist = Math.hypot(dx, dy) || 1;
            if (dist > radius) continue;
            const f = 1 - dist / radius;
            Matter.Sleeping.set(body, false);
            Matter.Body.setVelocity(body, {
              x: body.velocity.x + (dx / dist) * f * IMPULSE_PUSH,
              y: body.velocity.y - f * IMPULSE_UP,
            });
            Matter.Body.setAngularVelocity(
              body,
              body.angularVelocity + (Math.random() - 0.5) * 0.2 * f,
            );
          }
        };
        document.addEventListener('click', handleClick);

        // Fast fill: once the loaded name (row 0) has landed, spawn every
        // remaining row at once as a pre-stacked column above the viewport —
        // they cascade down in order (boosted gravity) and the whole fill
        // reads in about a second instead of row-by-row. Rows alternate
        // variant like the hero (row 0, the loaded name, counts as filled).
        // "Filled" is reported once every body has settled, with a timeout in
        // case something never quite stops jittering.
        let spawnTimer;
        let fillTimeout;
        let done = false;
        const rowSettled = row =>
          row.every(
            b =>
              culled.has(b) ||
              (b.position.y > 0 && (b.isSleeping || b.speed < SETTLE_SPEED)),
          );
        const finishFill = () => {
          if (done) return;
          done = true;
          clearInterval(spawnTimer);
          clearTimeout(fillTimeout);
          engine.gravity.y = 1; // back to normal for the scroll drain
          for (const s of sprites) Matter.Body.setInertia(s.body, s.baseInertia);
          onFilledRef.current?.();
        };
        const spawnRemainingRows = () => {
          for (let i = 1; i < totalRows; i++) {
            const useFilled = i % 2 === 0;
            // Stack the queued rows upward with a small air gap so the
            // solver never starts them overlapped.
            const y = -wordH - (i - 1) * (boxH + 4);
            rows.push([
              addSprite(
                useFilled ? firstFilled : firstOutline,
                firstX,
                y,
                firstW,
              ),
              addSprite(useFilled ? lastFilled : lastOutline, lastX, y, lastW),
            ]);
          }
          fillTimeout = setTimeout(finishFill, FILL_TIMEOUT_MS);
        };
        let cascadeSpawned = false;
        const checkFill = () => {
          if (!cascadeSpawned) {
            if (!rowSettled(rows[0])) return;
            cascadeSpawned = true;
            spawnRemainingRows();
            return;
          }
          if (rows.every(rowSettled)) finishFill();
        };

        // No visibility handling: a hidden tab gets no animation frames, so
        // the shared loop simply stops calling in, and Runner.tick discards
        // the long gap on return instead of simulating it.

        // Keep the fixed viewport canvas sized to the window; reposition the
        // fold-anchored floor and the side walls for the new width. Existing
        // piled bodies keep their document positions.
        const handleResize = () => {
          W = window.innerWidth;
          fold = window.innerHeight;
          docH = contentEnd();
          canvas.width = W;
          canvas.height = fold;
          // The split is a fraction of the width, so both sections have to be
          // rebuilt at the new size before updateFloor re-places the right one.
          const nextLeftW = W * FLOOR_SPLIT;
          const nextRightW = W - nextLeftW;
          Matter.Body.setPosition(leftFloor, {
            x: nextLeftW / 2,
            y: floorY,
          });
          Matter.Body.scale(leftFloor, nextLeftW / leftW, 1);
          Matter.Body.scale(rightFloor, nextRightW / rightW, 1);
          leftW = nextLeftW;
          rightW = nextRightW;
          Matter.Body.setPosition(leftWall, { x: -30, y: 0 });
          Matter.Body.setPosition(rightWall, { x: W + 30, y: 0 });
          lastShift = -1; // force floor reposition on the next scroll tick
          updateFloor();
        };
        window.addEventListener('resize', handleResize);

        // Handoff: draw the stationary 1:1 bodies first, then let the caller
        // hide the DOM name in that same frame, and step physics from the
        // next one on. The cascade spawns as soon as the loaded name (row 0)
        // has settled on the floor.
        //
        // Step, then draw, in one callback: the sprites always show the state
        // the solver just produced, where the separate Render and Runner
        // loops drew whichever state the previous frame had left behind.
        let stepping = false;
        leaveLoop = onFrame(
          now => {
            if (stepping) Matter.Runner.tick(runner, engine, now);
            draw();
            if (stepping || cancelled) return;
            stepping = true;
            onHandoffRef.current?.();
            spawnTimer = setInterval(checkFill, CHECK_MS);
          },
          { order: FRAME_ORDER.simulate },
        );

        teardown = () => {
          dropSource();
          if (spawnTimer) clearInterval(spawnTimer);
          if (fillTimeout) clearTimeout(fillTimeout);
          clearInterval(drainTimer);
          docObserver.disconnect();
          window.removeEventListener('scroll', updateFloor);
          window.removeEventListener('resize', handleResize);
          document.removeEventListener('click', handleClick);
          motionQuery.removeEventListener('change', handleMotionPreference);
          leaveLoop();
          Matter.Engine.clear(engine);
          sprites.length = 0;
          fireworks.length = 0;
        };
      },
    ).catch(error => {
      // Never strand the visitor on the loading screen.
      //
      // The landing sequence freezes scrolling until `onFilled` fires, and
      // `onFilled` is only reached from inside the block above. If the
      // matter-js chunk or one of the name rasterizations fails — an offline
      // reload with a cold cache, a hashed chunk 404 after a redeploy — none
      // of that runs, the loader overlay never unmounts, and the page is
      // permanently stuck at 100%.
      //
      // So on failure, hand off and release anyway: the hero loses its pile
      // of names and is simply empty, which is a survivable degradation, and
      // the rest of the page becomes reachable.
      if (cancelled) return;
      console.error('Landing physics failed to load', error);
      onHandoffRef.current?.();
      onFilledRef.current?.();
    });

    return () => {
      cancelled = true;
      teardown();
    };
  }, [active, getSpawnRect]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        // Above the sections, like the palm. The names are meant to fall INTO
        // the intro and glance off the palm's fronds on the way past — behind
        // the sections they simply vanished under the intro's paper ground.
        zIndex: 1,
      }}
    />
  );
};

FillPhysicsCanvas.propTypes = {
  active: PropTypes.bool.isRequired,
  getSpawnRect: PropTypes.func,
  onHandoff: PropTypes.func,
  onFilled: PropTypes.func,
};

export default FillPhysicsCanvas;
